import React from 'react';
import Layout from '../components/Layout';


function About() {

  return (
    <Layout maxWidth="max-w-6xl" bg="bg-primary-light dark:bg-primary-dark">
      <div className="max-w-6xl mx-auto">
        <div className="space-y-8 text-gray-700 dark:text-gray-300">
          <div className="flex items-start justify-between gap-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">Hello! I'm Shenqian Wen</h1>
              <p className="text-lg max-w-2xl flex-1">
                I'm a software engineer with a unique journey that led me from human resources to the exciting world of software development. Growing up in China, my path to technology wasn't linear—it was shaped by curiosity, hands-on experience, and a growing passion for data and problem-solving.
              </p>

              <p className="text-lg max-w-2xl flex-1 mt-4">
                Currently pursuing my Master's in Computer Science at Northeastern University, Seattle, I'm passionate about backend development and the transformative potential of AI technologies. I specialize in building intelligent applications that leverage Large Language Models (LLMs), Model Context Protocol (MCP), and AI agents to solve real-world problems and enhance user experiences.
              </p>
              <p className="text-lg max-w-2xl flex-1 mt-4">
                As a quick learner, problem solver, and collaborative team player, I'm eager to contribute to impactful software engineering projects. My diverse background provides me with unique perspectives on user needs and business requirements, making me a versatile asset in any development team.
              </p>
            </div>
            <img src="/aboutMe/aboutme_self.webp" alt="selfie" className="w-80 max-h-160 object-cover rounded-lg shadow-md dark:shadow-gray-700" />
          </div>

          <div>
            {/* <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">Fun Facts</h2> */}
            <div className="space-y-8">
              <p className="text-lg mt-4">
                If someone asks why I became a Software Developer, it wasn't my initial plan—rather, each choice along the way guided me to this path. During my HR master's program, I completed internships across various HR modules including recruiting, compensation, and training. However, my final internship was in HRIS (Human Resources Information Systems), which I landed because I had taken data analysis courses(learned SQL, python, etc.) from other program. After graduation, during my two years at a consulting firm, data analysis became my primary focus. In the course of my work, I began writing simple Python programs to automate routine tasks and improve efficiency. The joy I found in using code to enhance productivity was infectious—soon, I wasn't satisfied with just doing data analysis. Through this increasing exposure and hands-on coding experience, I discovered not only that I enjoyed it, but that I had a natural aptitude for it, leading to my career transition. Rather than saying I chose Computer Science, it's more accurate to say it guided me.
              </p>
            </div>
          </div>

          <div className="mt-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">Education</h2>
            <div className="space-y-8">
              <div className="border-l-4 border-gray-200 dark:border-gray-700 pl-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Northeastern University, Seattle</h3>
                    <p className="text-gray-600 dark:text-gray-400">Master's in Computer Science</p>
                  </div>
                  <div className="text-gray-500 dark:text-gray-400">Sep 2023 - May 2026</div>
                </div>
                <ul className="mt-2 text-gray-700 dark:text-gray-300 list-disc list-inside">
                  <li>Currently pursuing a master's degree in computer science, focusing on backend software engineering</li>
                  <li>Specializing in AI applications, Large Language Models, and intelligent agent systems</li>
                  <li>Strong focus on practical applications rather than theoretical research</li>
                </ul>
              </div>

              <div className="border-l-4 border-gray-200 dark:border-gray-700 pl-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">University of Minnesota Twin Cities</h3>
                    <p className="text-gray-600 dark:text-gray-400">Master's in Human Resource and Industrial Relations</p>
                  </div>
                  <div className="text-gray-500 dark:text-gray-400">2019 - 2021</div>
                </div>
                <ul className="mt-2 text-gray-700 dark:text-gray-300 list-disc list-inside">
                  <li>Gained experience across HR modules: recruiting, compensation, training, and HRIS</li>
                  <li>Developed strong analytical and problem-solving skills through data analysis coursework</li>
                  <li>Final internship in HRIS sparked interest in technology and data-driven solutions</li>
                </ul>
              </div>

              <div className="border-l-4 border-gray-200 dark:border-gray-700 pl-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Shanghai University</h3>
                    <p className="text-gray-600 dark:text-gray-400">Bachelor's Degree</p>
                  </div>
                  <div className="text-gray-500 dark:text-gray-400">2014 - 2018</div>
                </div>
                <ul className="mt-2 text-gray-700 dark:text-gray-300 list-disc list-inside">
                  <li>Established strong foundation in critical thinking and analysis</li>
                  <li>Developed communication and leadership skills</li>
                  <li>Built cultural bridge between Eastern and Western perspectives</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Relevant Coursework</h3>
                <div className="grid grid-cols-2 gap-4 text-gray-700 dark:text-gray-300">
                  <ul className="list-disc list-inside">
                    <li>Advanced Algorithms</li>
                    <li>Machine Learning</li>
                    <li>Database Systems</li>
                    <li>Backend Development</li>
                  </ul>
                  <ul className="list-disc list-inside">
                    <li>Natural Language Processing</li>
                    <li>Distributed Systems</li>
                    <li>Software Engineering</li>
                    <li>AI Agent Development</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">Technical Skills</h2>
            <div className="grid grid-cols-4 gap-8">
              <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg shadow-sm">
                <h3 className="font-medium text-gray-900 dark:text-white mb-3 text-lg">Languages</h3>
                <p>Python, Java, JavaScript, Go, C++, SQL</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg shadow-sm">
                <h3 className="font-medium text-gray-900 dark:text-white mb-3 text-lg">Frameworks</h3>
                <p>Node.js, Django, Spring Boot, React, FastAPI</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg shadow-sm">
                <h3 className="font-medium text-gray-900 dark:text-white mb-3 text-lg">Tools & Technologies</h3>
                <p>Git, Docker, AWS, MongoDB, PostgreSQL, Redis</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg shadow-sm">
                <h3 className="font-medium text-gray-900 dark:text-white mb-3 text-lg">AI & Specialized</h3>
                <p>LLMs, OpenAI API, MCP, AI Agents, Langchain</p>
              </div>
            </div>

            <div className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Areas of Interest & Expertise</h3>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Backend Development</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">Building scalable server-side applications, API design, microservices architecture, and database optimization.</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">AI Applications</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">Developing practical AI solutions using Large Language Models, building intelligent agents, and integrating AI into business workflows.</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Model Context Protocol (MCP)</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">Implementing MCP for seamless AI model integration and context management in enterprise applications.</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Data-Driven Solutions</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">Applying analytical thinking and business understanding to create user-centered technical solutions that address real-world challenges.</p>
                </div>
              </div>
            </div>
          </div>


        </div>
      </div>
    </Layout>
  );
}

export default About;